import { useState, useEffect, useCallback } from 'react';
import { Invoice } from '@/types/payments';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { useLatest } from '@/hooks/useLatest';

export interface UsePayablesDataResult {
  payables: Invoice[];
  setPayables: React.Dispatch<React.SetStateAction<Invoice[]>>;
  loading: boolean;
  refreshing: boolean;
  refreshData: () => Promise<void>;
}

export function usePayablesData(): UsePayablesDataResult {
  const t = useTranslations('consultantToasts');
  const tRef = useLatest(t);
  const [payables, setPayables] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Fetch payables on mount
  useEffect(() => {
    fetchPayables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPayables = useCallback(async () => {
    setLoading(true);
    try {
      const payablesData = await ServiceRegistry.getPaymentService().fetchPayables();
      setPayables(payablesData);
    } catch (error) {
      console.error('Error loading payables:', error);
      toast.error(tRef.current('common.error'), { description: tRef.current('loading.payablesFailed') });
      setPayables([]);
    } finally {
      setLoading(false);
    }
  }, [tRef]);

  const refreshData = useCallback(async () => {
    console.log('Refreshing data');
    if (refreshing) return;
    setRefreshing(true);
    toast(tRef.current('refresh.inProgressTitle'), { description: tRef.current('refresh.defaultInProgress') });
    try {
      await fetchPayables();
      toast.success(tRef.current('refresh.completeTitle'), { description: tRef.current('refresh.payablesUpdated') });
    } catch (error) {
      // Already handled in fetchPayables
    } finally {
      setRefreshing(false);
    }
  }, [fetchPayables, refreshing, tRef]);

  return {
    payables,
    setPayables,
    loading,
    refreshing,
    refreshData,
  };
}

export default usePayablesData; 